import { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { useToast } from '@/shared/components/ui/Toast';
import { AdminTable } from './AdminTable';
import { ConfirmDialog } from './ConfirmDialog';
import { useDeleteLocationManufacturerSettings } from '../hooks/useAdmin';
import type { LocationManufacturerSettingsWithDetails } from '../services/locationManufacturerSettingsService';

interface ManufacturerOverridesTableProps {
  overrides: LocationManufacturerSettingsWithDetails[];
  onEdit: (override: LocationManufacturerSettingsWithDetails) => void;
  onAdd: () => void;
  isLoading?: boolean;
}

const DAYS_OF_WEEK = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export function ManufacturerOverridesTable({
  overrides,
  onEdit,
  onAdd,
  isLoading = false,
}: ManufacturerOverridesTableProps) {
  const { showToast } = useToast();
  const [deleteOverride, setDeleteOverride] =
    useState<LocationManufacturerSettingsWithDetails | null>(null);
  const deleteMutation = useDeleteLocationManufacturerSettings();

  const handleDelete = async () => {
    if (!deleteOverride) return;

    try {
      await deleteMutation.mutateAsync(deleteOverride.id);
      showToast('Manufacturer override deleted successfully', 'success');
      setDeleteOverride(null);
    } catch (error) {
      console.error('Error deleting manufacturer override:', error);
      showToast(
        error instanceof Error
          ? error.message
          : 'Failed to delete manufacturer override',
        'error'
      );
    }
  };

  const columns = [
    {
      key: 'manufacturer',
      header: 'Manufacturer',
      sortable: true,
      render: (override: LocationManufacturerSettingsWithDetails) => (
        <div>
          <div className="font-medium">{override.manufacturer.name}</div>
          <div className="text-xs text-gray-500">
            Global: Qty {override.manufacturer.min_order_qty || 'N/A'} | $
            {override.manufacturer.min_order_amt?.toFixed(2) || 'N/A'}
          </div>
        </div>
      ),
    },
    {
      key: 'min_order_qty',
      header: 'Min Qty Override',
      render: (override: LocationManufacturerSettingsWithDetails) => (
        <div className="text-sm">
          {override.min_order_qty !== null
            ? override.min_order_qty
            : 'Using global'}
        </div>
      ),
    },
    {
      key: 'min_order_amt',
      header: 'Min Amount Override',
      render: (override: LocationManufacturerSettingsWithDetails) => (
        <div className="text-sm">
          {override.min_order_amt !== null
            ? `$${override.min_order_amt.toFixed(2)}`
            : 'Using global'}
        </div>
      ),
    },
    {
      key: 'free_ship_amt',
      header: 'Free Ship Override',
      render: (override: LocationManufacturerSettingsWithDetails) => (
        <div className="text-sm">
          {override.free_ship_amt !== null
            ? `$${override.free_ship_amt.toFixed(2)}`
            : 'Using global'}
        </div>
      ),
    },
    {
      key: 'preferred_ordering_day',
      header: 'Preferred Day',
      render: (override: LocationManufacturerSettingsWithDetails) => (
        <div className="text-sm">
          {override.preferred_ordering_day !== null
            ? DAYS_OF_WEEK[override.preferred_ordering_day]
            : 'No preference'}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (override: LocationManufacturerSettingsWithDetails) => (
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => onEdit(override)}>
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDeleteOverride(override)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Manufacturer Overrides
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Override global manufacturer settings for this location
        </p>
      </div>

      <AdminTable
        data={overrides}
        columns={columns}
        onAdd={onAdd}
        addLabel="Add Override"
        isLoading={isLoading}
        emptyMessage="No manufacturer overrides configured"
      />

      <ConfirmDialog
        isOpen={!!deleteOverride}
        onClose={() => setDeleteOverride(null)}
        onConfirm={handleDelete}
        title="Delete Manufacturer Override"
        message={`Are you sure you want to delete the override for "${deleteOverride?.manufacturer.name}"? Global settings will be used instead.`}
        confirmLabel="Delete"
        confirmVariant="danger"
        isLoading={deleteMutation.isPending}
      />
    </>
  );
}
